/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useState, useMemo } from "react";

/**
 * groupConfig — array of group definitions:
 * [
 *   {
 *     type: "service",           // unique key for this group
 *     label: "Services",         // display label
 *     extract: (item) => item.services,  // fn to get sub-array from entity
 *     idField: "service_id",     // field used as unique ID in sub-array
 *     nameField: "name",         // field used as display name (default "name")
 *   },
 * ]
 *
 * Filtering logic:
 *   - Within a group: OR  (item matches if it has ANY of the selected IDs)
 *   - Between groups:  AND (item must match all groups that have active filters)
 */
export const useFilterCheckbox = (items = [], groupConfig = []) => {
  const [selectedFilters, setSelectedFilters] = useState([]);

  const toggleFilter = (type, id) => {
    setSelectedFilters((prev) =>
      prev.some((f) => f.type === type && f.id === id)
        ? prev.filter((f) => !(f.type === type && f.id === id))
        : [...prev, { type, id }]
    );
  };

  const clearFilters = () => setSelectedFilters([]);

  const filterGroups = useMemo(() => {
    return groupConfig.map(({ type, label, extract, idField, nameField = "name" }) => {
      const seen = new Map();
      items.forEach((item) => {
        extract(item)?.forEach((sub) => {
          const id = sub[idField];
          if (id !== undefined && !seen.has(id)) seen.set(id, sub[nameField]);
        });
      });
      return {
        type,
        label,
        items: Array.from(seen, ([id, name]) => ({ id, name })),
      };
    });
  }, [items, groupConfig]);

  const filteredItems = useMemo(() => {
    if (!selectedFilters.length) return items;

    return items.filter((item) =>
      groupConfig.every(({ type, extract, idField }) => {
        const activeIds = selectedFilters.filter((f) => f.type === type).map((f) => f.id);
        if (!activeIds.length) return true;
        return extract(item)?.some((sub) => activeIds.includes(sub[idField]));
      })
    );
  }, [items, selectedFilters, groupConfig]);

  return { filterGroups, selectedFilters, toggleFilter, clearFilters, filteredItems };
};